import type { ModuleAccessLevelEnum, RefType } from "@/lib/lecturehub/types";

/**
 * Every "what stands between this reader and this item" decision, in one
 * place, so the course card, the media and event sidebars, the paywall, the
 * article gate and checkout cannot drift apart on it.
 *
 * THE MODEL — the admin sets every content type the same way:
 *
 *   Free  + Everyone            accessLevel PUBLIC
 *   Free  + Registered members  accessLevel REGISTERED
 *   Paid  (price in cents)      accessLevel PREMIUM, free = false, price > 0
 *                               — single-payment portals only
 *   Subscribers only            accessLevel PREMIUM — subscription portals,
 *                               which have no prices
 *
 * The backend decides access alone (`Module.canView`): PUBLIC is anyone,
 * REGISTERED any signed-in account, PREMIUM an enrolment in the item (single,
 * which buying grants) or an active subscription. Price never decides access,
 * so it only decides what a locked item OFFERS — and a price is shown only on
 * an item this reader must buy (`saleOffer`), never on one that opens without
 * paying.
 *
 * Nobody enrols by hand. A free course enrols in the background on "Start"
 * (CourseStartCard); everything else plays the moment `canView` is true —
 * videos, podcasts and events have no enrol button at all (2026-10-20: the
 * old "free, but enrol first" items were converted to Registered).
 */
export type Access =
  /** `canView`: show it. */
  | { kind: "open" }
  /** Signed out, and an account is what opens it (or may: a user-group
   *  restriction can only be judged once signed in). */
  | { kind: "login" }
  /** Single-payment portal, a paid item: price + add to cart. */
  | { kind: "buy"; price: number }
  /** Subscription portal, subscribers only. Never a price. */
  | { kind: "subscribe" }
  /** Signed in, a PREMIUM COURSE marked free: a free enrolment opens it, and
   *  "Začni" makes one anyway. Only courses saved before the admin's Free/Paid
   *  switch land here — new free items are PUBLIC or REGISTERED. */
  | { kind: "start" }
  /** Signed in and still locked, with nothing to offer: a user group, or a
   *  type that cannot be sold. */
  | { kind: "restricted" };

/** The types a reader can buy or enrol in (`enrollModule`, checkout). Articles
 *  cannot be sold yet (lecturehub-api#289); FAQs and collections never are. */
export const SELLABLE_TYPES: readonly RefType[] = ["course", "event", "ott", "podcast"];

/** Never behind money of any kind — Everyone or Registered members only. */
const NEVER_PAID: readonly RefType[] = ["faq", "collection"];

type Priced = {
  accessLevel?: ModuleAccessLevelEnum | null;
  /** The admin's Free / Paid switch. */
  free?: boolean | null;
  /** Cents, like every `price` in the schema. */
  price?: number | null;
};

/** PREMIUM, or not told — the field is nullable, and an absent value is
 *  "unknown", never "no restriction". */
function isPremium(accessLevel: ModuleAccessLevelEnum | null | undefined): boolean {
  return accessLevel !== "PUBLIC" && accessLevel !== "REGISTERED";
}

/** The price of a paid item on a single-payment portal, or null: PREMIUM,
 *  not marked free, a positive price. A price left behind on an item that
 *  is now Free (or PUBLIC/REGISTERED) is not one. */
function salePrice(m: Priced): number | null {
  if (!isPremium(m.accessLevel) || m.free === true) return null;
  return m.price != null && Number(m.price) > 0 ? Number(m.price) : null;
}

export function moduleAccess(
  input: Priced & {
    canView: boolean | null | undefined;
    isAuthed: boolean;
    isSubscription: boolean;
    refType: RefType | null | undefined;
  },
): Access {
  // `null` is not a refusal: only an explicit false withholds the item.
  if (input.canView !== false) return { kind: "open" };
  const sellable = !!input.refType && SELLABLE_TYPES.includes(input.refType);
  const premium = isPremium(input.accessLevel);
  // A subscription opens every subscribers-only item, articles included; it
  // sells none of them, so it is offered wherever money may gate at all.
  if (premium && input.isSubscription && !NEVER_PAID.includes(input.refType as RefType)) {
    return { kind: "subscribe" };
  }
  if (sellable && premium) {
    const price = salePrice(input);
    if (price != null) return { kind: "buy", price };
  }
  if (!input.isAuthed) return { kind: "login" };
  if (input.refType === "course" && premium && !input.isSubscription && input.free === true) {
    return { kind: "start" };
  }
  return { kind: "restricted" };
}

/**
 * The "Free" badge on cards, which have no viewer (`canView`) to ask: the item
 * opens without paying — Everyone or Registered members, or a PREMIUM item the
 * admin marked free. Paid and subscribers-only items carry no badge; cards
 * never print a price (decision 2026-10-20: a price shows only where this
 * reader must buy, which only the detail page knows).
 */
export function isFreeToUnlock(m: Priced): boolean {
  return !isPremium(m.accessLevel) || m.free === true;
}
